import { useEffect, useRef } from "react";
import { useAuth, useOrganizationList } from "@clerk/react";

/**
 * Selects an initial Clerk organization before application API queries begin.
 * Company workspaces win over personal workspaces; Clerk public metadata is only
 * a selection hint here, never an authorization source.
 */
export const useOrganizationBootstrap = (enabled = true) => {
  const { isLoaded: authLoaded, isSignedIn, orgId } = useAuth();
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { pageSize: 100 },
  });
  const activating = useRef(false);

  useEffect(() => {
    if (!enabled || !authLoaded || !isSignedIn || orgId || !isLoaded) return;
    if (userMemberships.isLoading || activating.current) return;
    const memberships = userMemberships.data || [];
    if (memberships.length === 0) return;

    const preferred =
      memberships.find(
        (membership: { organization: { publicMetadata?: Record<string, unknown> } }) =>
          membership.organization.publicMetadata?.isPersonal !== true
      ) || memberships[0];
    activating.current = true;
    void setActive({ organization: preferred.organization.id }).finally(() => {
      activating.current = false;
    });
  }, [
    authLoaded,
    enabled,
    isLoaded,
    isSignedIn,
    orgId,
    setActive,
    userMemberships.data,
    userMemberships.isLoading,
  ]);

  if (!enabled || !authLoaded || !isSignedIn || orgId) return true;
  if (!isLoaded || userMemberships.isLoading) return false;
  return (userMemberships.data?.length || 0) === 0;
};
